/**
 * CSAT-Historie am Ticket (Spec 8.6 / 7.2 „Kundenkontext-Panel").
 *
 * Zeigt, was angefragt und was beantwortet wurde — beides, weil eine
 * unbeantwortete Anfrage genauso eine Information ist wie eine Note.
 */
import { CSAT_MAX_SCORE, type CsatResponse } from '@tickethub/shared';
import { Star } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

type CsatPanelProps = {
  items: CsatResponse[];
  /** `false` = CSAT ist fuer diesen Mandanten aus (Spec 3.2). */
  enabled: boolean;
  disabled: boolean;
  onRequest: () => Promise<void>;
  locale: string;
};

export function CsatPanel({ items, enabled, disabled, onRequest, locale }: CsatPanelProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  if (!enabled) return null;

  const formatter = new Intl.DateTimeFormat(locale, { dateStyle: 'short' });
  const openRequest = items.some((entry) => entry.responded_at === null);

  async function request() {
    setBusy(true);
    try {
      await onRequest();
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3 rounded-lg border p-4" aria-label={t('csat.title')}>
      <h2 className="text-sm font-medium">{t('csat.title')}</h2>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('csat.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((entry) => (
            <li key={entry.id} className="space-y-1 text-sm">
              {entry.score === null ? (
                <Badge variant="outline">{t('csat.pending')}</Badge>
              ) : (
                <span className="flex items-center gap-1">
                  <Star aria-hidden="true" className="size-4 text-amber-500" />
                  {t('csat.score', { score: entry.score, max: CSAT_MAX_SCORE })}
                </span>
              )}
              {entry.comment ? (
                <p className="text-xs text-muted-foreground">„{entry.comment}"</p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {formatter.format(new Date(entry.responded_at ?? entry.requested_at))}
              </p>
            </li>
          ))}
        </ul>
      )}

      {!disabled ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy || openRequest}
          onClick={() => void request()}
        >
          {openRequest ? t('csat.already_requested') : t('csat.request')}
        </Button>
      ) : null}
    </section>
  );
}
